import { useCallback, useEffect, useRef, useState } from "react";
import { useRecoilValue } from "recoil";

import { useUser } from "MVVM/Hooks/useUser";
import { StripePaystationState } from "MVVM/State";

const { REACT_APP_STRIPE_ENDPOINT } = process.env;

type TTrialCardStatus = {
  trialing: boolean;
  hasCard: boolean;
  trialEnd: number | null;
  extended: boolean;
  reactivationEligible: boolean;
  reactivationDeadline: number | null;
};

/**
 * Whether the signed-in trial user has a card attached to their trialing
 * subscription — and, after expiry, whether the 7 bonus days are still
 * claimable (ALL-1838). Answered by the payment endpoint from live Stripe
 * data — default_payment_method, the extension stamp, and the expired sub's
 * timing aren't on the user record, and a localStorage marker wouldn't
 * survive a second device.
 */
export const useTrialCardStatus = () => {
  const { allstarUser } = useUser();
  const paystation = useRecoilValue(StripePaystationState);
  const userGroup = allstarUser?.user?.userGroup;
  const isTrialPeriod = !!userGroup?.isTrialPeriod;
  // Post-expiry claim candidates: had a trial, currently below the trial tier.
  // Mirrors the server's cheap gate so users with no trial history never make
  // the request at all.
  const mayReactivate =
    !!userGroup?.hasUsedPlatinumTrial &&
    userGroup?.type !== "CREATOR_PLAT" &&
    userGroup?.type !== "INTERNAL";
  const shouldFetch = isTrialPeriod || mayReactivate;
  const token = allstarUser?.token;

  const [status, setStatus] = useState<TTrialCardStatus | null>(null);
  const [loading, setLoading] = useState(shouldFetch);

  const refetch = useCallback(async () => {
    if (!shouldFetch || !token) {
      setStatus(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `${REACT_APP_STRIPE_ENDPOINT}/stripe/v1/request?trialCardStatus=1`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json();
      if (res.ok) {
        setStatus({
          trialing: !!data.trialing,
          hasCard: !!data.hasCard,
          trialEnd: data.trialEnd ?? null,
          extended: !!data.extended,
          reactivationEligible: !!data.reactivationEligible,
          reactivationDeadline: data.reactivationDeadline ?? null,
        });
      }
    } catch {
      // Leave status null — surfaces gated on it simply don't render.
    } finally {
      setLoading(false);
    }
  }, [shouldFetch, token]);

  // Refetched (not just fetched on mount) because the answer changes mid-
  // session: adding a card in the paystation flips hasCard/extended, but the
  // always-mounted consumers (countdown banner, dropdown card, clip-status
  // CTA) would otherwise keep pitching "add a card, get 7 bonus days" to a
  // user who just claimed them. expiresMs re-syncs too — the card-attach
  // webhook moves the trial expiry, so a refreshed user record with a stale
  // card status is exactly the drifted state to catch.
  const expiresMs = userGroup?.expires
    ? new Date(userGroup.expires).getTime()
    : null;
  useEffect(() => {
    refetch();
  }, [refetch, expiresMs]);

  const modalWasOpen = useRef(false);
  useEffect(() => {
    if (paystation.isModalOpen) {
      modalWasOpen.current = true;
      return;
    }
    if (!modalWasOpen.current) return;
    modalWasOpen.current = false;
    refetch();
    // The webhook that attaches the card lags the checkout completing; one
    // delayed retry catches it without polling.
    const retry = setTimeout(refetch, 8000);
    return () => clearTimeout(retry);
  }, [paystation.isModalOpen, refetch]);

  return { status, loading, refetch };
};
